import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import * as api from '../services/api'
import { VersionConflictError } from '../services/api'

// Default statuses for new projects
const DEFAULT_STATUSES = [
  { id: 'backlog', name: 'Backlog', showOnBoard: false, isFinal: false, isDefault: true },
  { id: 'todo', name: 'To Do', showOnBoard: true, isFinal: false, isDefault: false },
  { id: 'in progress', name: 'In Progress', showOnBoard: true, isFinal: false, isDefault: false },
  { id: 'hold', name: 'Hold', showOnBoard: true, isFinal: false, isDefault: false },
  { id: 'done', name: 'Done', showOnBoard: true, isFinal: true, isDefault: false }
]

// Helper function to recursively find a task by ID
const findTaskById = (tasks, id) => {
  for (const task of tasks) {
    if (task.id === id) {
      return task
    }
    if (task.subtasks && task.subtasks.length > 0) {
      const found = findTaskById(task.subtasks, id)
      if (found) return found
    }
  }
  return null
}

// Helper function to recursively find and update a task within a project's tasks
const findAndUpdateTask = (tasks, id, updateFn) => {
  return tasks.map((task) => {
    if (task.id === id) {
      return updateFn(task)
    }
    if (task.subtasks && task.subtasks.length > 0) {
      return {
        ...task,
        subtasks: findAndUpdateTask(task.subtasks, id, updateFn),
      }
    }
    return task
  })
}

// Helper function to recursively find and remove a task
const findAndRemoveTask = (tasks, id) => {
  return tasks
    .filter((task) => task.id !== id)
    .map((task) => {
      if (task.subtasks && task.subtasks.length > 0) {
        return {
          ...task,
          subtasks: findAndRemoveTask(task.subtasks, id),
        }
      }
      return task
    })
}

// Helper function to recursively filter completed tasks
const filterCompleted = (tasks) => {
  return tasks
    .filter((task) => !task.completed)
    .map((task) => {
      if (task.subtasks && task.subtasks.length > 0) {
        return {
          ...task,
          subtasks: filterCompleted(task.subtasks),
        }
      }
      return task
    })
}

// Helper function to recursively collect task IDs that have subtasks
// filterFn determines which tasks with subtasks to include
const collectTaskIdsWithSubtasks = (tasks, filterFn) => {
  const ids = []
  const traverse = (taskList) => {
    for (const task of taskList) {
      if (task.subtasks && task.subtasks.length > 0) {
        if (filterFn(task)) {
          ids.push({ id: task.id, rowVersion: task.rowVersion })
        }
        traverse(task.subtasks)
      }
    }
  }
  traverse(tasks)
  return ids
}

// Helper function to find the parent epic for a given task ID
// Returns the epic task object if found, or null if no epic ancestor exists
const findParentEpic = (tasks, targetId, ancestorEpic = null) => {
  for (const task of tasks) {
    // Update the current epic if this task is an epic
    const currentEpic = task.isEpic ? task : ancestorEpic

    // If this is the target task, return the current epic
    if (task.id === targetId) {
      return currentEpic
    }

    // Search in subtasks
    if (task.subtasks && task.subtasks.length > 0) {
      const result = findParentEpic(task.subtasks, targetId, currentEpic)
      if (result !== undefined) {
        return result
      }
    }
  }
  return undefined // Not found in this branch
}

// Helper function to parse JSON string fields from API response
const parseTaskFields = (task) => {
  let boardOrder = {}
  let assignees = []
  let labels = []
  let dependencies = []

  // Parse boardOrder from JSON string
  if (task.boardOrder) {
    try {
      boardOrder = typeof task.boardOrder === 'string'
        ? JSON.parse(task.boardOrder)
        : task.boardOrder
    } catch {
      boardOrder = {}
    }
  }

  // Parse assignees from JSON string
  if (task.assignees) {
    try {
      assignees = typeof task.assignees === 'string'
        ? JSON.parse(task.assignees)
        : task.assignees
    } catch {
      assignees = []
    }
  }

  // Parse labels from JSON string
  if (task.labels) {
    try {
      labels = typeof task.labels === 'string'
        ? JSON.parse(task.labels)
        : task.labels
    } catch {
      labels = []
    }
  }

  // Parse dependencies from JSON string (array of task IDs)
  if (task.dependencies) {
    try {
      dependencies = typeof task.dependencies === 'string'
        ? JSON.parse(task.dependencies)
        : task.dependencies
    } catch {
      dependencies = []
    }
  }

  // Parse planned dates (string or null)
  const plannedStartDate = task.plannedStartDate || null
  const plannedEndDate = task.plannedEndDate || null

  // Parse reminderInterval (string, defaults to 'default')
  const reminderInterval = task.reminderInterval || 'default'

  return {
    ...task,
    boardOrder,
    assignees,
    labels,
    dependencies,
    plannedStartDate,
    plannedEndDate,
    reminderInterval,
    subtasks: [] // Initialize empty subtasks array, will be populated by buildTaskHierarchy
  }
}

// Helper function to build task hierarchy from flat array
// Takes flat array of tasks with parentTaskId and returns nested structure with subtasks
const buildTaskHierarchy = (flatTasks) => {
  // First, parse all task fields and create a map for quick lookup
  const taskMap = new Map()
  const parsedTasks = flatTasks.map(task => {
    const parsed = parseTaskFields(task)
    taskMap.set(parsed.id, parsed)
    return parsed
  })

  // Build the hierarchy
  const rootTasks = []

  for (const task of parsedTasks) {
    if (task.parentTaskId === null || task.parentTaskId === undefined) {
      // Root level task
      rootTasks.push(task)
    } else {
      // Child task - add to parent's subtasks
      const parent = taskMap.get(task.parentTaskId)
      if (parent) {
        parent.subtasks.push(task)
      } else {
        // Parent not found, treat as root task
        rootTasks.push(task)
      }
    }
  }

  // Sort tasks at each level by sortOrder
  const sortByOrder = (tasks) => {
    tasks.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    for (const task of tasks) {
      if (task.subtasks && task.subtasks.length > 0) {
        sortByOrder(task.subtasks)
      }
    }
  }

  sortByOrder(rootTasks)

  return rootTasks
}

// Helper function to flatten hierarchical tasks into batch update format
// Returns array of { id, sortOrder, parentTaskId, rowVersion }
const flattenTasksForBatchUpdate = (tasks, parentTaskId = null) => {
  const updates = []
  tasks.forEach((task, index) => {
    updates.push({
      id: task.id,
      sortOrder: index,
      parentTaskId: parentTaskId,
      rowVersion: task.rowVersion
    })
    if (task.subtasks && task.subtasks.length > 0) {
      updates.push(...flattenTasksForBatchUpdate(task.subtasks, task.id))
    }
  })
  return updates
}

const useProjectStore = create(
  persist(
    (set, get) => ({
      // Projects from API (structure matches API response)
      projects: [],

      // Loading and error states for API operations
      loading: false,
      error: null,

      // Version conflict state (set when API returns 409 conflict)
      versionConflict: null,

      // UI state for tasks (global, not project-specific)
      selectedTaskIds: [],
      activeSubtaskFormId: null,
      activeSubtaskFormData: { input: '', dueDate: '', assignees: [], status: 'backlog', priority: '', storyPoints: '', details: '' },
      moveError: null,

      // Expanded state for tasks (browser-only, persisted to localStorage)
      // Map of taskId -> boolean (false = collapsed, true or undefined = expanded)
      expandedTaskIds: {},

      // Attachment cache (not persisted - fetched from server)
      taskAttachments: {}, // { [taskId]: Attachment[] }

      // Watched task IDs (not persisted - fetched fresh from server)
      watchedTaskIds: new Set(),

      // ============ API Operations ============

      fetchProjects: async () => {
        set({ loading: true, error: null })
        try {
          const projects = await api.fetchProjects()
          set({ projects, loading: false })
          return projects
        } catch (error) {
          set({ error: error.message, loading: false })
          throw error
        }
      },

      createProject: async (name, description = '') => {
        set({ loading: true, error: null })
        try {
          const settings = {
            description,
            statuses: DEFAULT_STATUSES
          }
          const project = await api.createProject(name, settings)
          set((state) => ({
            projects: [project, ...state.projects],
            loading: false
          }))
          return project.id
        } catch (error) {
          set({ error: error.message, loading: false })
          throw error
        }
      },

      updateProject: async (id, updates) => {
        set({ loading: true, error: null })
        try {
          const currentProject = get().getProject(id)
          const rowVersion = currentProject?.rowVersion
          const project = await api.updateProject(id, updates, rowVersion)
          set((state) => ({
            projects: state.projects.map((p) => get()._matchesId(p.id, id) ? project : p),
            loading: false
          }))
          return project
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchProjects()
            set({
              versionConflict: { type: 'project', message: error.message },
              loading: false
            })
            return
          }
          set({ error: error.message, loading: false })
          throw error
        }
      },

      deleteProject: async (id) => {
        set({ loading: true, error: null })
        try {
          const currentProject = get().getProject(id)
          const rowVersion = currentProject?.rowVersion
          await api.deleteProject(id, rowVersion)
          set((state) => ({
            projects: state.projects.filter((p) => !get()._matchesId(p.id, id)),
            loading: false
          }))
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchProjects()
            set({
              versionConflict: { type: 'project', message: error.message },
              loading: false
            })
            return
          }
          set({ error: error.message, loading: false })
          throw error
        }
      },

      refreshProject: async (id) => {
        try {
          const project = await api.getProject(id)
          set((state) => ({
            projects: state.projects.map((p) => get()._matchesId(p.id, id) ? project : p)
          }))
          return project
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      clearError: () => set({ error: null }),

      setVersionConflict: (conflict) => set({ versionConflict: conflict }),

      clearVersionConflict: () => set({ versionConflict: null }),

      // ============ Local Project Helpers ============

      // Helper to compare IDs (handles string/number conversion from route params vs API)
      _matchesId: (a, b) => {
        if (a === b) return true
        if (a == null || b == null) return false
        return String(a) === String(b)
      },

      getProject: (id) => {
        const state = get()
        return state.projects.find((p) => get()._matchesId(p.id, id))
      },

      // Inject or update project data for MyTasks page
      // This allows TaskItem to work with projects that may not be fully loaded
      injectProjectData: (projectData) => {
        set((state) => {
          const existingIndex = state.projects.findIndex(p =>
            get()._matchesId(p.id, projectData.id)
          )

          if (existingIndex === -1) {
            // Add new project
            return {
              projects: [...state.projects, {
                id: projectData.id,
                name: projectData.name,
                settings: {
                  statuses: projectData.statuses || [],
                  tasks: projectData.tasks || [],
                  labels: projectData.labels || []
                },
                permission: projectData.permission || 'Member'
              }]
            }
          } else {
            // Update existing project - merge data but preserve existing fields
            const updated = [...state.projects]
            updated[existingIndex] = {
              ...updated[existingIndex],
              name: projectData.name || updated[existingIndex].name,
              settings: {
                ...updated[existingIndex].settings,
                statuses: projectData.statuses || updated[existingIndex].settings?.statuses || [],
                tasks: projectData.tasks || updated[existingIndex].settings?.tasks || [],
                labels: projectData.labels || updated[existingIndex].settings?.labels || []
              }
            }
            return { projects: updated }
          }
        })
      },

      // ============ Permission Helpers ============

      // Get current user's permission for a project
      getCurrentUserPermission: (projectId) => {
        const project = get().getProject(projectId)
        return project?.permission || null
      },

      // Permission check helpers
      canManageProject: (projectId) => {
        const permission = get().getCurrentUserPermission(projectId)
        return permission === 'Owner'
      },

      canEditTasks: (projectId) => {
        const permission = get().getCurrentUserPermission(projectId)
        return ['Owner', 'Member'].includes(permission)
      },

      isReadOnly: (projectId) => {
        const permission = get().getCurrentUserPermission(projectId)
        return permission === 'Read Only'
      },

      // ============ Status Helpers ============

      getStatuses: (projectId) => {
        const state = get()
        const project = state.projects.find((p) => get()._matchesId(p.id, projectId))
        return project?.settings?.statuses || DEFAULT_STATUSES
      },

      getDefaultStatus: (projectId) => {
        const statuses = get().getStatuses(projectId)
        const defaultStatusObj = statuses.find(s => s.isDefault)
        return defaultStatusObj?.id || 'backlog'
      },

      getBoardStatuses: (projectId) => {
        const statuses = get().getStatuses(projectId)
        return statuses.filter(s => s.showOnBoard)
      },

      getFinalStatuses: (projectId) => {
        const statuses = get().getStatuses(projectId)
        return statuses.filter(s => s.isFinal).map(s => s.id)
      },

      getLabels: (projectId) => {
        const state = get()
        const project = state.projects.find((p) => get()._matchesId(p.id, projectId))
        return project?.settings?.labels || []
      },

      addLabel: async (projectId, { name, color }) => {
        const state = get()
        const project = state.projects.find((p) => get()._matchesId(p.id, projectId))
        if (!project) return null

        const newLabel = {
          name: name.trim(),
          color
        }

        const currentLabels = project.settings?.labels || []
        const updatedLabels = [...currentLabels, newLabel]

        try {
          await api.updateProject(projectId, {
            settings: { ...project.settings, labels: updatedLabels }
          }, project.rowVersion)

          // Refresh the project to get updated data
          await get().refreshProject(projectId)

          return newLabel
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      // ============ Task Selection State ============

      setSelectedTaskIds: (ids) => set({ selectedTaskIds: ids }),

      toggleSelectedTask: (id) => set((state) => {
        const isSelected = state.selectedTaskIds.includes(id)
        return {
          selectedTaskIds: isSelected
            ? state.selectedTaskIds.filter(taskId => taskId !== id)
            : [...state.selectedTaskIds, id]
        }
      }),

      clearSelectedTasks: () => set({ selectedTaskIds: [] }),

      // ============ Subtask Form State ============

      setActiveSubtaskFormId: (id) => set({ activeSubtaskFormId: id }),

      setActiveSubtaskFormData: (data) => set({ activeSubtaskFormData: data }),

      clearActiveSubtaskForm: () => set({
        activeSubtaskFormId: null,
        activeSubtaskFormData: { input: '', dueDate: '', assignees: [], status: 'backlog', priority: '', storyPoints: '', details: '' }
      }),

      // ============ Move Error State ============

      clearMoveError: () => set({ moveError: null }),

      // ============ Task Operations (API-backed - tasks stored in settings.tasks) ============

      // Fetch tasks from API and update local cache
      // API returns flat array with parentTaskId - we build hierarchy locally
      fetchTasks: async (projectId) => {
        try {
          const flatTasks = await api.fetchTasks(projectId)
          // Build nested hierarchy from flat array and parse JSON fields
          const tasks = buildTaskHierarchy(flatTasks)
          set((state) => ({
            projects: state.projects.map(p => {
              if (!get()._matchesId(p.id, projectId)) return p
              return {
                ...p,
                settings: {
                  ...p.settings,
                  tasks
                }
              }
            })
          }))
          // After fetching tasks successfully, also fetch which ones the user is watching
          get().fetchWatchedTasks(projectId)
          return tasks
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      getProjectTasks: (projectId) => {
        const state = get()
        const project = state.projects.find((p) => get()._matchesId(p.id, projectId))
        return project?.settings?.tasks || []
      },

      getParentEpic: (projectId, taskId) => {
        const state = get()
        const project = state.projects.find((p) => get()._matchesId(p.id, projectId))
        if (!project) return null
        const tasks = project.settings?.tasks || []
        const epic = findParentEpic(tasks, taskId)
        return epic || null
      },

      // Helper to update local tasks cache after API operations
      _updateLocalTasks: (projectId, tasks) => {
        set((state) => ({
          projects: state.projects.map(p => {
            if (!get()._matchesId(p.id, projectId)) return p
            return {
              ...p,
              settings: {
                ...p.settings,
                tasks
              },
              updated_at: new Date().toISOString()
            }
          })
        }))
      },

      addTask: async (projectId, text, parentId = null, dueDate = null, assignees = [], status = null, priority = null, storyPoints = null, details = null, isEpic = false, labels = [], plannedStartDate = null, plannedEndDate = null, dependencies = []) => {
        const state = get()
        const defaultStatus = state.getDefaultStatus(projectId)
        const taskStatus = status || defaultStatus

        try {
          await api.createTask(projectId, {
            text,
            parentId,
            dueDate,
            assignees,
            status: taskStatus,
            priority,
            storyPoints,
            details,
            isEpic,
            labels: JSON.stringify(labels),
            plannedStartDate,
            plannedEndDate,
            dependencies: JSON.stringify(dependencies)
          })
          // Refresh tasks from server to get updated list
          await get().fetchTasks(projectId)
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      removeTask: async (projectId, taskId) => {
        try {
          const tasks = get().getProjectTasks(projectId)
          const task = findTaskById(tasks, taskId)
          const rowVersion = task?.rowVersion
          await api.deleteTask(projectId, taskId, rowVersion)
          // Refresh tasks from server
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      toggleTask: async (projectId, taskId, finalStatusId = 'done', defaultStatusId = 'backlog', finalStatusIds = ['done']) => {
        const tasks = get().getProjectTasks(projectId)
        const task = findTaskById(tasks, taskId)
        if (!task) return

        const newCompletedState = !task.completed
        const completedDate = newCompletedState ? Date.now() : null
        const newStatus = newCompletedState
          ? finalStatusId
          : (finalStatusIds.includes(task.status) ? defaultStatusId : task.status)

        // Build subtasks with updated completion state
        const markAllSubtasks = (subtasks, completed) => {
          const now = completed ? Date.now() : null
          const status = completed ? finalStatusId : defaultStatusId
          return subtasks.map((t) => ({
            ...t,
            completed,
            completedDate: now,
            status,
            subtasks: t.subtasks ? markAllSubtasks(t.subtasks, completed) : [],
          }))
        }

        const updates = {
          completed: newCompletedState,
          completedDate,
          status: newStatus,
          subtasks: task.subtasks ? markAllSubtasks(task.subtasks, newCompletedState) : []
        }

        try {
          await api.updateTask(projectId, taskId, updates, task.rowVersion)
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      clearCompleted: async (projectId) => {
        const tasks = get().getProjectTasks(projectId)

        // Find all completed tasks with their rowVersions (recursively)
        const findCompletedTasks = (taskList) => {
          const found = []
          for (const task of taskList) {
            if (task.completed) {
              found.push({ id: task.id, rowVersion: task.rowVersion })
            }
            if (task.subtasks && task.subtasks.length > 0) {
              found.push(...findCompletedTasks(task.subtasks))
            }
          }
          return found
        }

        const completedTasks = findCompletedTasks(tasks)

        try {
          // Delete each completed task
          for (const { id: taskId, rowVersion } of completedTasks) {
            await api.deleteTask(projectId, taskId, rowVersion)
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      updateTaskText: async (projectId, taskId, newText, dueDate = undefined, assignees = undefined, status = undefined, isFinalStatus = undefined, priority = undefined, storyPoints = undefined, details = undefined, isEpic = undefined, labels = undefined, plannedStartDate = undefined, plannedEndDate = undefined, dependencies = undefined, reminderInterval = undefined) => {
        const tasks = get().getProjectTasks(projectId)
        const task = findTaskById(tasks, taskId)
        if (!task) return

        const updates = {
          text: newText,
          ...(dueDate !== undefined && { dueDate }),
          ...(assignees !== undefined && { assignees }),
          ...(priority !== undefined && { priority }),
          ...(storyPoints !== undefined && { storyPoints }),
          ...(details !== undefined && { details }),
          ...(isEpic !== undefined && { isEpic }),
          ...(labels !== undefined && { labels: JSON.stringify(labels) }),
          ...(plannedStartDate !== undefined && { plannedStartDate }),
          ...(plannedEndDate !== undefined && { plannedEndDate }),
          ...(dependencies !== undefined && { dependencies: JSON.stringify(dependencies) }),
          ...(reminderInterval !== undefined && { reminderInterval }),
        }

        if (status !== undefined) {
          updates.status = status
          const markAsComplete = isFinalStatus !== undefined ? isFinalStatus : (status === 'done')
          if (markAsComplete && !task.completed) {
            updates.completed = true
            updates.completedDate = Date.now()
          } else if (!markAsComplete && task.completed) {
            updates.completed = false
            updates.completedDate = null
          } else if (markAsComplete) {
            updates.completed = true
          }
        }

        try {
          await api.updateTask(projectId, taskId, updates, task.rowVersion)
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      updateMultipleTasksStatus: async (projectId, ids, status, isFinalStatus = undefined) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue

            const updates = { status }
            const markAsComplete = isFinalStatus !== undefined ? isFinalStatus : (status === 'done')
            if (markAsComplete && !task.completed) {
              updates.completed = true
              updates.completedDate = Date.now()
            } else if (!markAsComplete && task.completed) {
              updates.completed = false
              updates.completedDate = null
            } else if (markAsComplete) {
              updates.completed = true
            }

            await api.updateTask(projectId, id, updates, task.rowVersion)
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      assignTasksToUser: async (projectId, ids, userName) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue

            const currentAssignees = task.assignees || []
            // Only add if not already assigned
            if (!currentAssignees.includes(userName)) {
              await api.updateTask(projectId, id, {
                assignees: [...currentAssignees, userName]
              }, task.rowVersion)
            }
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      unassignAllTasks: async (projectId, ids) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue

            await api.updateTask(projectId, id, {
              assignees: []
            }, task.rowVersion)
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      updateMultipleTasksPriority: async (projectId, ids, priority) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue

            await api.updateTask(projectId, id, {
              priority: priority
            }, task.rowVersion)
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      updateMultipleTasksDueDate: async (projectId, ids, dueDate) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue

            await api.updateTask(projectId, id, {
              dueDate: dueDate
            }, task.rowVersion)
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      updateMultipleTasksEpic: async (projectId, ids, isEpic) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue

            await api.updateTask(projectId, id, {
              isEpic: isEpic
            }, task.rowVersion)
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      duplicateTask: async (projectId, taskId) => {
        const tasks = get().getProjectTasks(projectId)
        const task = findTaskById(tasks, taskId)
        if (!task) return

        try {
          await api.createTask(projectId, {
            text: `Copy ${task.text}`,
            parentId: task.parentTaskId || null,
            dueDate: task.dueDate || null,
            assignees: task.assignees || [],
            status: task.status,
            priority: task.priority || null,
            storyPoints: task.storyPoints || null,
            details: task.details || null,
            isEpic: task.isEpic || false
          })
          await get().fetchTasks(projectId)
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      duplicateTaskWithSubtasks: async (projectId, taskId) => {
        const tasks = get().getProjectTasks(projectId)
        const task = findTaskById(tasks, taskId)
        if (!task) return

        // Helper to recursively create tasks
        const createTaskRecursively = async (sourceTask, parentId, isRoot = false) => {
          const newTaskData = {
            text: isRoot ? `Copy ${sourceTask.text}` : sourceTask.text,
            parentId: parentId,
            dueDate: sourceTask.dueDate || null,
            assignees: sourceTask.assignees || [],
            status: sourceTask.status,
            priority: sourceTask.priority || null,
            storyPoints: sourceTask.storyPoints || null,
            details: sourceTask.details || null,
            isEpic: sourceTask.isEpic || false
          }

          const newTask = await api.createTask(projectId, newTaskData)

          // Recursively create subtasks
          if (sourceTask.subtasks && sourceTask.subtasks.length > 0) {
            for (const subtask of sourceTask.subtasks) {
              await createTaskRecursively(subtask, newTask.id, false)
            }
          }

          return newTask
        }

        try {
          await createTaskRecursively(task, task.parentTaskId || null, true)
          await get().fetchTasks(projectId)
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      removeMultipleTasks: async (projectId, ids) => {
        const tasks = get().getProjectTasks(projectId)

        try {
          for (const id of ids) {
            const task = findTaskById(tasks, id)
            if (!task) continue
            await api.deleteTask(projectId, id, task.rowVersion)
          }
          await get().fetchTasks(projectId)
          // Clear selection after deletion
          set({ selectedTaskIds: [] })
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      // Check if a task is expanded (defaults to true if not in the map)
      isTaskExpanded: (taskId) => {
        const state = get()
        return state.expandedTaskIds[taskId] !== false
      },

      toggleExpanded: (projectId, taskId) => {
        set((state) => ({
          expandedTaskIds: {
            ...state.expandedTaskIds,
            [taskId]: state.expandedTaskIds[taskId] === false ? true : false
          }
        }))
      },

      expandAllTasks: (projectId) => {
        const tasks = get().getProjectTasks(projectId)
        const idsToExpand = {}

        const collectIds = (taskList) => {
          for (const task of taskList) {
            if (task.subtasks && task.subtasks.length > 0) {
              idsToExpand[task.id] = true
              collectIds(task.subtasks)
            }
          }
        }
        collectIds(tasks)

        set((state) => ({
          expandedTaskIds: {
            ...state.expandedTaskIds,
            ...idsToExpand
          }
        }))
      },

      collapseAllTasks: (projectId) => {
        const tasks = get().getProjectTasks(projectId)
        const idsToCollapse = {}

        const collectIds = (taskList) => {
          for (const task of taskList) {
            if (task.subtasks && task.subtasks.length > 0) {
              idsToCollapse[task.id] = false
              collectIds(task.subtasks)
            }
          }
        }
        collectIds(tasks)

        set((state) => ({
          expandedTaskIds: {
            ...state.expandedTaskIds,
            ...idsToCollapse
          }
        }))
      },

      reorderTasks: async (projectId, parentId, draggedTaskId, targetTaskId, position) => {
        const tasks = get().getProjectTasks(projectId)
        let newTasks

        if (parentId === null) {
          // Reorder root level tasks
          newTasks = [...tasks]
          const draggedIndex = newTasks.findIndex(t => t.id === draggedTaskId)
          const targetIndex = newTasks.findIndex(t => t.id === targetTaskId)

          if (draggedIndex === -1 || targetIndex === -1) return

          const [removed] = newTasks.splice(draggedIndex, 1)
          let insertIndex = targetIndex
          if (draggedIndex < targetIndex) {
            insertIndex = position === 'after' ? targetIndex : targetIndex - 1
          } else {
            insertIndex = position === 'after' ? targetIndex + 1 : targetIndex
          }
          newTasks.splice(insertIndex, 0, removed)

          // Sync root level reorder to server using batch update
          try {
            // Only need to update sortOrder for root level tasks that changed
            const updates = newTasks.map((task, index) => ({
              id: task.id,
              sortOrder: index,
              parentTaskId: null,
              rowVersion: task.rowVersion
            }))
            const result = await api.batchUpdateTasks(projectId, updates)
            if (!result.success && result.error === 'VERSION_CONFLICT') {
              await get().fetchTasks(projectId)
              set({ versionConflict: { type: 'task', message: 'Task was modified by someone else.' } })
              return
            }
            await get().fetchTasks(projectId)
          } catch (error) {
            if (error instanceof VersionConflictError) {
              await get().fetchTasks(projectId)
              set({ versionConflict: { type: 'task', message: error.message } })
              return
            }
            set({ error: error.message })
            throw error
          }
        } else {
          // Reorder subtasks within a parent
          const parent = findTaskById(tasks, parentId)
          if (!parent) return

          const newSubtasks = [...(parent.subtasks || [])]
          const draggedIndex = newSubtasks.findIndex(t => t.id === draggedTaskId)
          const targetIndex = newSubtasks.findIndex(t => t.id === targetTaskId)

          if (draggedIndex === -1 || targetIndex === -1) return

          const [removed] = newSubtasks.splice(draggedIndex, 1)
          let insertIndex = targetIndex
          if (draggedIndex < targetIndex) {
            insertIndex = position === 'after' ? targetIndex : targetIndex - 1
          } else {
            insertIndex = position === 'after' ? targetIndex + 1 : targetIndex
          }
          newSubtasks.splice(insertIndex, 0, removed)

          // Update subtasks order using batch update
          try {
            const updates = newSubtasks.map((task, index) => ({
              id: task.id,
              sortOrder: index,
              parentTaskId: parentId,
              rowVersion: task.rowVersion
            }))
            const result = await api.batchUpdateTasks(projectId, updates)
            if (!result.success && result.error === 'VERSION_CONFLICT') {
              await get().fetchTasks(projectId)
              set({ versionConflict: { type: 'task', message: 'Task was modified by someone else.' } })
              return
            }
            await get().fetchTasks(projectId)
          } catch (error) {
            if (error instanceof VersionConflictError) {
              await get().fetchTasks(projectId)
              set({ versionConflict: { type: 'task', message: error.message } })
              return
            }
            set({ error: error.message })
            throw error
          }
        }
      },

      moveTaskToParent: async (projectId, todoId, sourceParentId, targetParentId, targetIndex) => {
        // Prevent moving a task to be its own parent
        if (todoId === targetParentId) {
          set({ moveError: 'Cannot move a task into itself.' })
          return
        }

        const project = get().getProject(projectId)
        if (!project) return

        const tasks = project.settings?.tasks || []

        // Helper to check if targetId is a descendant of sourceId
        const isDescendant = (taskList, sourceId, targetId) => {
          for (const task of taskList) {
            if (task.id === sourceId) {
              const checkSubtasks = (subtasks) => {
                if (!subtasks) return false
                for (const subtask of subtasks) {
                  if (subtask.id === targetId) return true
                  if (checkSubtasks(subtask.subtasks)) return true
                }
                return false
              }
              return checkSubtasks(task.subtasks)
            }
            if (task.subtasks && isDescendant(task.subtasks, sourceId, targetId)) {
              return true
            }
          }
          return false
        }

        // Prevent moving a parent into its own descendant
        if (targetParentId !== null && isDescendant(tasks, todoId, targetParentId)) {
          set({ moveError: 'Cannot move a task into its own subtask.' })
          return
        }

        let taskToMove = null

        // Helper to find and remove task from source
        const removeTaskAndCapture = (taskList, id, currentParentId) => {
          if (currentParentId === null) {
            const index = taskList.findIndex(t => t.id === id)
            if (index !== -1) {
              taskToMove = taskList[index]
              return taskList.filter(t => t.id !== id)
            }
            return taskList.map(task => ({
              ...task,
              subtasks: task.subtasks ? removeTaskAndCapture(task.subtasks, id, null) : []
            }))
          } else {
            return taskList.map(task => {
              if (task.id === currentParentId) {
                const index = (task.subtasks || []).findIndex(t => t.id === id)
                if (index !== -1) {
                  taskToMove = task.subtasks[index]
                  return {
                    ...task,
                    subtasks: task.subtasks.filter(t => t.id !== id)
                  }
                }
              }
              return {
                ...task,
                subtasks: task.subtasks ? removeTaskAndCapture(task.subtasks, id, currentParentId) : []
              }
            })
          }
        }

        // Remove from source
        let newTasks = removeTaskAndCapture(tasks, todoId, sourceParentId)

        if (!taskToMove) return

        // Add to target
        if (targetParentId === null) {
          newTasks.splice(targetIndex, 0, taskToMove)
        } else {
          newTasks = findAndUpdateTask(newTasks, targetParentId, (parent) => {
            const newSubtasks = [...(parent.subtasks || [])]
            newSubtasks.splice(targetIndex, 0, taskToMove)
            return {
              ...parent,
              subtasks: newSubtasks,
              isExpanded: true,
            }
          })
        }

        // Sync the restructured tasks to server using batch update
        try {
          // Flatten the entire task tree to get all tasks with updated sortOrder and parentTaskId
          const updates = flattenTasksForBatchUpdate(newTasks)
          const result = await api.batchUpdateTasks(projectId, updates)
          if (!result.success && result.error === 'VERSION_CONFLICT') {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: 'Task was modified by someone else.' } })
            return
          }
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      updateTaskBoardOrder: async (projectId, taskId, newStatus, newBoardOrder, isFinalStatus = undefined) => {
        const tasks = get().getProjectTasks(projectId)
        const task = findTaskById(tasks, taskId)
        if (!task) return

        const updates = {}

        if (newStatus !== undefined && newStatus !== task.status) {
          updates.status = newStatus
          const markAsComplete = isFinalStatus !== undefined ? isFinalStatus : (newStatus === 'done')
          if (markAsComplete && !task.completed) {
            updates.completed = true
            updates.completedDate = Date.now()
          } else if (!markAsComplete && task.completed) {
            updates.completed = false
            updates.completedDate = null
          } else if (markAsComplete) {
            updates.completed = true
          }
        }

        if (newBoardOrder !== undefined) {
          updates.boardOrder = {
            ...(task.boardOrder || {}),
            [newStatus || task.status]: newBoardOrder
          }
        }

        try {
          await api.updateTask(projectId, taskId, updates, task.rowVersion)
          await get().fetchTasks(projectId)
        } catch (error) {
          if (error instanceof VersionConflictError) {
            await get().fetchTasks(projectId)
            set({ versionConflict: { type: 'task', message: error.message } })
            return
          }
          set({ error: error.message })
          throw error
        }
      },

      // ============ Attachment Operations ============

      // Fetch attachments for a task
      fetchTaskAttachments: async (projectId, taskId) => {
        try {
          const attachments = await api.fetchAttachments(projectId, taskId)
          set((state) => ({
            taskAttachments: {
              ...state.taskAttachments,
              [taskId]: attachments
            }
          }))
          return attachments
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      // Upload attachments to a task
      uploadTaskAttachments: async (projectId, taskId, files) => {
        try {
          const result = await api.uploadAttachments(projectId, taskId, files)
          // Refresh attachments for this task
          await get().fetchTaskAttachments(projectId, taskId)
          return result.attachments
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      // Delete an attachment
      deleteTaskAttachment: async (projectId, taskId, attachmentId) => {
        try {
          await api.deleteAttachment(projectId, attachmentId)
          // Refresh attachments for this task
          await get().fetchTaskAttachments(projectId, taskId)
        } catch (error) {
          set({ error: error.message })
          throw error
        }
      },

      // Get cached attachments for a task (synchronous)
      getTaskAttachments: (taskId) => {
        return get().taskAttachments[taskId] || []
      },

      // Get attachment count for a task (synchronous)
      getTaskAttachmentCount: (taskId) => {
        return (get().taskAttachments[taskId] || []).length
      },

      // Clear attachments from cache (for cleanup)
      clearTaskAttachments: (taskId) => {
        set((state) => {
          const { [taskId]: _, ...rest } = state.taskAttachments
          return { taskAttachments: rest }
        })
      },

      // ============ Task Watch Operations ============

      // Fetch watched task IDs for a project
      fetchWatchedTasks: async (projectId) => {
        try {
          const response = await api.fetchWatchedTasks(projectId)
          set({ watchedTaskIds: new Set(response.taskIds) })
        } catch (error) {
          console.error('Failed to fetch watched tasks:', error)
        }
      },

      // Watch a task
      watchTask: async (taskId) => {
        try {
          await api.watchTask(taskId)
          set(state => ({
            watchedTaskIds: new Set([...state.watchedTaskIds, taskId])
          }))
        } catch (error) {
          console.error('Failed to watch task:', error)
          throw error
        }
      },

      // Unwatch a task
      unwatchTask: async (taskId) => {
        try {
          await api.unwatchTask(taskId)
          set(state => {
            const newSet = new Set(state.watchedTaskIds)
            newSet.delete(taskId)
            return { watchedTaskIds: newSet }
          })
        } catch (error) {
          console.error('Failed to unwatch task:', error)
          throw error
        }
      },

      // Check if watching a task
      isWatching: (taskId) => {
        return get().watchedTaskIds.has(taskId)
      },
    }),
    {
      name: 'project-storage',
      partialize: (state) => ({
        projects: state.projects,
        activeSubtaskFormId: state.activeSubtaskFormId,
        activeSubtaskFormData: state.activeSubtaskFormData,
        moveError: state.moveError,
        expandedTaskIds: state.expandedTaskIds,
      }),
    }
  )
)

export default useProjectStore
export { DEFAULT_STATUSES }
